import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, ChevronLeft, ChevronRight, Save, Check } from "lucide-react";
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from "recharts";
import MgmtLayout from "../MgmtLayout";
import { api } from "../../api";

const inputCls = "border border-ink/15 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-matcha-dark bg-bone";

const BOTTLE_START_G = 100;

const shiftDate = (dateStr, delta) => {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const monthOf = (dateStr) => dateStr.slice(0, 7);

const computeUsage = (b) => Math.max(0, BOTTLE_START_G - Number(b.leftover_yesterday_g || 0));
const computeTopup = (b) => Math.max(0, BOTTLE_START_G - Number(b.leftover_yesterday_g || 0));

function BottleSection({ kind, bottles, setBottles, accent, grades = [] }) {
  const label = kind === "matcha" ? "Matcha bottles"
              : kind === "hojicha" ? "Hojicha bottles"
                                   : "Coffee bottles";
  const totalUsage = bottles.filter((b) => b.kind === kind).reduce((s, b) => s + computeUsage(b), 0);
  const addBottle = () => {
    const existing = bottles.filter((b) => b.kind === kind).length;
    const num = String(existing + 1).padStart(2, "0");
    setBottles([
      ...bottles,
      {
        key: `${kind}-${Date.now()}-${existing}`,
        label: `Bottle ${num}`,
        kind,
        leftover_yesterday_g: 0,
        leftover_today_g: 0,
      },
    ]);
  };
  const patch = (idx, k, v) =>
    setBottles(bottles.map((b, i) => (i === idx ? { ...b, [k]: v } : b)));
  const remove = (idx) => setBottles(bottles.filter((_, i) => i !== idx));

  const kindBottles = bottles
    .map((b, idx) => ({ ...b, _idx: idx }))
    .filter((b) => b.kind === kind);

  return (
    <section className="mb-8" data-testid={`bottle-section-${kind}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <p className="overline text-matcha-dark">{label}</p>
          <p className="text-xs text-ink-soft mt-0.5">Each bottle starts fresh at {BOTTLE_START_G}g daily · Topup = 100 − yesterday&apos;s leftover</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-ink-soft">
            Used <span className={`font-display text-lg ${accent}`} data-testid={`total-usage-${kind}`}>{totalUsage.toFixed(0)}g</span>
          </span>
          <button
            type="button"
            onClick={addBottle}
            data-testid={`add-bottle-${kind}`}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 bg-ink text-bone text-[10px] uppercase tracking-[0.18em] hover:bg-matcha-dark transition-colors"
          >
            <Plus size={12} /> Add bottle
          </button>
        </div>
      </div>

      {kindBottles.length === 0 ? (
        <div className="border border-dashed border-ink/15 rounded-sm p-6 text-center text-sm text-ink-soft" data-testid={`empty-${kind}`}>
          No {kind} bottles today. Click &quot;Add bottle&quot; to start.
        </div>
      ) : (
        <div className="border border-ink/10 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream/60 text-left text-ink-soft">
              <tr>
                <th className="px-4 py-2 overline">Bottle</th>
                <th className="px-4 py-2 overline">Grade used (100g)</th>
                <th className="px-4 py-2 overline text-right">Leftover yesterday (g)</th>
                <th className="px-4 py-2 overline text-right">Topup today (g)</th>
                <th className="px-4 py-2 overline text-right">Start today (g)</th>
                <th className="px-4 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {kindBottles.map((b) => {
                const topup = computeTopup(b);
                return (
                  <tr key={b.key} data-testid={`bottle-row-${b.key}`} className="border-t border-ink/5">
                    <td className="px-4 py-3">
                      <input
                        data-testid={`bottle-label-${b.key}`}
                        className={inputCls + " w-28 text-sm"}
                        value={b.label}
                        onChange={(e) => patch(b._idx, "label", e.target.value)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <select
                        data-testid={`bottle-grade-${b.key}`}
                        className={inputCls + " text-sm w-full max-w-[180px]"}
                        value={b.variety_id || ""}
                        onChange={(e) => patch(b._idx, "variety_id", e.target.value)}
                      >
                        <option value="">— pick grade —</option>
                        {grades.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                            {g.stock_qty != null ? ` · ${Number(g.stock_qty).toFixed(1)} ${g.unit || ""}` : ""}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input
                        data-testid={`bottle-lefty-${b.key}`}
                        type="number" min="0" step="0.1" max="200"
                        className={inputCls + " w-24 text-right"}
                        value={b.leftover_yesterday_g}
                        onChange={(e) => patch(b._idx, "leftover_yesterday_g", Number(e.target.value))}
                      />
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-matcha-dark" data-testid={`bottle-topup-${b.key}`}>
                      +{topup.toFixed(1).replace(/\.0$/, "")}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold" data-testid={`bottle-start-${b.key}`}>
                      {BOTTLE_START_G}
                    </td>
                    <td className="px-2 py-3 text-right">
                      <button
                        type="button"
                        data-testid={`bottle-del-${b.key}`}
                        onClick={() => remove(b._idx)}
                        className="text-ink-soft/40 hover:text-red-700"
                        title="Remove bottle"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Chart({ month, onMonthChange, refreshKey }) {
  const [data, setData] = useState({ days: [], totals: null });
  const [autoBump, setAutoBump] = useState(0);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/mgmt/daily-powder-chart", { params: { month } });
      setData({ days: data.days || [], totals: data.totals || null });
    })();
  }, [month, refreshKey, autoBump]);

  // Auto-refresh when the browser tab regains focus — so taps logged on
  // /staff/quick-usage (another tab or earlier page) show up immediately
  // when the admin flips back to the chart.
  useEffect(() => {
    const onFocus = () => setAutoBump((n) => n + 1);
    const onVis = () => { if (document.visibilityState === "visible") setAutoBump((n) => n + 1); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const { days, totals } = data;

  const rows = days.map((d) => ({
    day: String(d.day).padStart(2, "0"),
    matcha_g: d.matcha_g,
    hojicha_g: d.hojicha_g,
    coffee_g: d.coffee_g || 0,
    matcha_cups: d.matcha_cups,
    hojicha_cups: d.hojicha_cups,
    complimentary_cups: d.complimentary_cups || 0,
    orders: d.orders,
    has_log: d.has_log,
  }));

  const t = totals || { matcha_g: 0, hojicha_g: 0, total_g: 0, matcha_cups: 0, hojicha_cups: 0, complimentary_cups: 0, days_logged: 0, days_in_month: 0, avg_matcha_g_per_cup: null, avg_hojicha_g_per_cup: null };

  return (
    <div data-testid="chart-container">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <input
          data-testid="chart-month"
          type="month"
          className={inputCls}
          value={month}
          onChange={(e) => onMonthChange(e.target.value)}
        />
        <button
          type="button"
          data-testid="chart-refresh"
          onClick={() => setAutoBump((n) => n + 1)}
          className="text-[10px] uppercase tracking-[0.14em] text-matcha-dark hover:underline"
          title="Refetch latest usage"
        >
          ↻ Refresh
        </button>
        <span className="text-xs text-ink-soft" data-testid="chart-days-logged">
          {t.days_logged} of {t.days_in_month} days logged
        </span>
      </div>

      {/* Big month total banner */}
      <div className="bg-ink text-bone rounded-sm p-6 mb-4 flex items-baseline justify-between flex-wrap gap-4" data-testid="month-total-banner">
        <div>
          <p className="overline text-bone/60 text-[10px]">Month total usage</p>
          <p className="font-display text-4xl mt-1" data-testid="stat-month-total">
            {t.total_g.toFixed(0)}<span className="text-lg text-bone/60 ml-1">g</span>
          </p>
        </div>
        <div className="flex items-baseline gap-8">
          <div>
            <p className="overline text-bone/60 text-[10px]">Matcha</p>
            <p className="font-display text-2xl mt-1 text-sage" data-testid="stat-month-matcha-g">{t.matcha_g.toFixed(0)}<span className="text-sm text-bone/60 ml-1">g</span></p>
          </div>
          <div>
            <p className="overline text-bone/60 text-[10px]">Hojicha</p>
            <p className="font-display text-2xl mt-1 text-amber-300" data-testid="stat-month-hojicha-g">{t.hojicha_g.toFixed(0)}<span className="text-sm text-bone/60 ml-1">g</span></p>
          </div>
          <div>
            <p className="overline text-bone/60 text-[10px]">Coffee</p>
            <p className="font-display text-2xl mt-1 text-stone-300" data-testid="stat-month-coffee-g">{(t.coffee_g || 0).toFixed(0)}<span className="text-sm text-bone/60 ml-1">g</span></p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-cream/40 border border-ink/10 rounded-sm p-4">
          <p className="overline text-ink-soft text-[10px]">Matcha cups</p>
          <p className="font-display text-2xl text-matcha-dark mt-1" data-testid="stat-month-matcha-cups">{t.matcha_cups}</p>
        </div>
        <div className="bg-cream/40 border border-ink/10 rounded-sm p-4">
          <p className="overline text-ink-soft text-[10px]">Hojicha cups</p>
          <p className="font-display text-2xl text-amber-800 mt-1" data-testid="stat-month-hojicha-cups">{t.hojicha_cups}</p>
        </div>
        <div className="bg-cream/40 border border-ink/10 rounded-sm p-4">
          <p className="overline text-ink-soft text-[10px]">Complimentary</p>
          <p className="font-display text-2xl text-rose-700 mt-1" data-testid="stat-month-comp-cups">{t.complimentary_cups || 0}</p>
        </div>
        <div className="bg-cream/40 border border-ink/10 rounded-sm p-4">
          <p className="overline text-ink-soft text-[10px]">Matcha g / cup</p>
          <p className="font-display text-2xl text-ink mt-1" data-testid="stat-matcha-per-cup">{t.avg_matcha_g_per_cup ?? "—"}<span className="text-sm text-ink-soft ml-1">g</span></p>
        </div>
        <div className="bg-cream/40 border border-ink/10 rounded-sm p-4">
          <p className="overline text-ink-soft text-[10px]">Hojicha g / cup</p>
          <p className="font-display text-2xl text-ink mt-1" data-testid="stat-hojicha-per-cup">{t.avg_hojicha_g_per_cup ?? "—"}<span className="text-sm text-ink-soft ml-1">g</span></p>
        </div>
      </div>

      <div className="bg-bone border border-ink/10 rounded-sm p-4" style={{ height: 360 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="#e0dcd0" strokeDasharray="3 3" />
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#6b6b6b" }} interval={0} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#6b6b6b" }} label={{ value: "grams", angle: -90, position: "insideLeft", fontSize: 11, fill: "#6b6b6b" }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#6b6b6b" }} label={{ value: "cups / orders", angle: 90, position: "insideRight", fontSize: 11, fill: "#6b6b6b" }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="left" dataKey="matcha_g" fill="#5b7a3f" name="Matcha (g)" stackId="usage" />
            <Bar yAxisId="left" dataKey="hojicha_g" fill="#a06f2e" name="Hojicha (g)" stackId="usage" />
            <Bar yAxisId="left" dataKey="coffee_g" fill="#1e40af" name="Coffee (g)" stackId="usage" />
            <Line yAxisId="right" type="monotone" dataKey="matcha_cups" stroke="#2f5233" strokeWidth={2} dot={{ r: 3 }} name="Matcha cups" />
            <Line yAxisId="right" type="monotone" dataKey="hojicha_cups" stroke="#7a4a1e" strokeWidth={2} dot={{ r: 3 }} name="Hojicha cups" />
            <Line yAxisId="right" type="monotone" dataKey="complimentary_cups" stroke="#be123c" strokeWidth={2} dot={{ r: 3 }} name="Complimentary" />
            <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#1a1a1a" strokeDasharray="4 4" strokeWidth={1.5} dot={false} name="Orders" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-ink-soft mt-2">
        X-axis covers the entire month. Days without a log show as 0-bars (no data recorded).
      </p>
    </div>
  );
}

export default function MgmtDailyPowder() {
  const [date, setDate] = useState(todayISO());
  const [chartMonth, setChartMonth] = useState(monthOf(todayISO()));
  const [chartRefresh, setChartRefresh] = useState(0);
  const [bottles, setBottles] = useState([]);
  const [grades, setGrades] = useState({ matcha: [], hojicha: [], coffee: [] });
  const [sales, setSales] = useState({ matcha_cups: 0, hojicha_cups: 0, complimentary_cups: 0 });
  const [salesReal, setSalesReal] = useState({ orders_count: 0, revenue: 0 });
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setErr(""); setOk("");
    const { data } = await api.get(`/mgmt/daily-powder/${date}`);
    setBottles(data.bottles || []);
    setSales(data.sales || { matcha_cups: 0, hojicha_cups: 0, complimentary_cups: 0 });
    setSalesReal(data.sales_real || { orders_count: 0, revenue: 0 });
    setExists(data.exists !== false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  // Load the matcha/hojicha/coffee grade dropdown once on mount.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/mgmt/powder-grades");
        setGrades({
          matcha:  data.matcha  || [],
          hojicha: data.hojicha || [],
          coffee:  data.coffee  || [],
        });
      } catch { /* silent — dropdown stays empty */ }
    })();
  }, []);

  // Refresh sales_real when tab becomes visible again (e.g., after keying sales in another tab).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        load();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
    // eslint-disable-next-line
  }, [date]);

  const totals = useMemo(() => {
    let matcha = 0, hojicha = 0, coffee = 0;
    bottles.forEach((b) => {
      const u = computeUsage(b);
      if (b.kind === "matcha") matcha += u;
      else if (b.kind === "coffee") coffee += u;
      else hojicha += u;
    });
    return { matcha_g: matcha, hojicha_g: hojicha, coffee_g: coffee, total_g: matcha + hojicha + coffee };
  }, [bottles]);

  const save = async () => {
    setErr(""); setOk(""); setBusy(true);
    try {
      await api.put(`/mgmt/daily-powder/${date}`, {
        bottles: bottles.map(({ key, label, kind, leftover_yesterday_g, leftover_today_g, variety_id }) => ({
          key, label, kind,
          leftover_yesterday_g: Number(leftover_yesterday_g || 0),
          leftover_today_g: Number(leftover_today_g || 0),
          variety_id: variety_id || "",
        })),
        sales: {
          matcha_cups: Number(sales.matcha_cups || 0),
          hojicha_cups: Number(sales.hojicha_cups || 0),
          complimentary_cups: Number(sales.complimentary_cups || 0),
        },
      });
      setOk(exists ? "Overwritten" : "Saved");
      setExists(true);
      setChartRefresh((n) => n + 1);
      setTimeout(() => setOk(""), 2000);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const markNoRecord = async () => {
    if (exists && !window.confirm("This will remove the saved log for " + date + ". Continue?")) return;
    setErr(""); setOk(""); setBusy(true);
    try {
      await api.delete(`/mgmt/daily-powder/${date}`);
      setBottles([]);
      setSales({ matcha_cups: 0, hojicha_cups: 0, complimentary_cups: 0 });
      setExists(false);
      setOk("Marked no record");
      setChartRefresh((n) => n + 1);
      setTimeout(() => setOk(""), 2000);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <MgmtLayout subtitle="Daily Powder" title="Matcha · Hojicha · Coffee">
      <div className="p-6 md:p-10 max-w-6xl">
        {/* Date navigator */}
        <div className="flex items-center gap-3 mb-8">
          <button type="button" onClick={() => setDate(shiftDate(date, -1))} data-testid="date-prev" className="p-2 rounded-full border border-ink/15 hover:border-ink transition-colors" title="Previous day"><ChevronLeft size={14} /></button>
          <input
            data-testid="date-picker"
            type="date"
            className={inputCls}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <button type="button" onClick={() => setDate(shiftDate(date, 1))} data-testid="date-next" className="p-2 rounded-full border border-ink/15 hover:border-ink transition-colors" title="Next day"><ChevronRight size={14} /></button>
          <button type="button" onClick={() => setDate(todayISO())} data-testid="date-today" className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">Today</button>
          <span className="ml-4 text-xs text-ink-soft" data-testid="date-status">
            {exists ? <span className="text-matcha-dark">● Saved</span> : <span className="text-amber-800">○ Not saved yet</span>}
          </span>
        </div>

        <BottleSection kind="matcha" bottles={bottles} setBottles={setBottles} accent="text-matcha-dark" grades={grades.matcha} />
        <BottleSection kind="hojicha" bottles={bottles} setBottles={setBottles} accent="text-amber-800" grades={grades.hojicha} />
        <BottleSection kind="coffee" bottles={bottles} setBottles={setBottles} accent="text-stone-700" grades={grades.coffee} />

        {/* Sales section */}
        <section className="mb-8" data-testid="sales-section">
          <p className="overline text-matcha-dark mb-3">Daily sales</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div className="bg-cream/40 border border-ink/10 rounded-sm p-4 relative">
              <p className="overline text-ink-soft text-[10px]">Orders (auto)</p>
              <p className="font-display text-2xl text-ink mt-1" data-testid="sales-orders-real">{salesReal.orders_count}</p>
              <p className="text-[10px] text-ink-soft mt-0.5">from Sales & Orders</p>
              <button
                type="button"
                data-testid="refresh-sales-btn"
                onClick={load}
                className="absolute top-2 right-2 text-[10px] uppercase tracking-[0.14em] text-matcha-dark hover:underline"
                title="Re-pull sales for this date"
              >
                ↻ Refresh
              </button>
            </div>
            <div className="bg-cream/40 border border-ink/10 rounded-sm p-4">
              <p className="overline text-ink-soft text-[10px]">Revenue (auto)</p>
              <p className="font-display text-2xl text-matcha-dark mt-1" data-testid="sales-revenue-real">RM {Number(salesReal.revenue || 0).toFixed(2)}</p>
            </div>
            <div className="bg-bone border-2 border-matcha-dark/40 rounded-sm p-4">
              <label className="overline text-matcha-dark text-[10px] block">Matcha cups (manual)</label>
              <input
                data-testid="sales-matcha-cups"
                type="number"
                min="0"
                className={inputCls + " w-full mt-1 text-2xl font-display text-matcha-dark border-0 p-0 focus:ring-0"}
                value={sales.matcha_cups}
                onChange={(e) => setSales({ ...sales, matcha_cups: Number(e.target.value) })}
              />
              <p className="text-[10px] text-ink-soft mt-0.5">→ {totals.matcha_g > 0 && Number(sales.matcha_cups) > 0 ? `${(totals.matcha_g / sales.matcha_cups).toFixed(2)}g / cup` : "—"}</p>
            </div>
            <div className="bg-bone border-2 border-amber-800/40 rounded-sm p-4">
              <label className="overline text-amber-800 text-[10px] block">Hojicha cups (manual)</label>
              <input
                data-testid="sales-hojicha-cups"
                type="number"
                min="0"
                className={inputCls + " w-full mt-1 text-2xl font-display text-amber-800 border-0 p-0 focus:ring-0"}
                value={sales.hojicha_cups}
                onChange={(e) => setSales({ ...sales, hojicha_cups: Number(e.target.value) })}
              />
              <p className="text-[10px] text-ink-soft mt-0.5">→ {totals.hojicha_g > 0 && Number(sales.hojicha_cups) > 0 ? `${(totals.hojicha_g / sales.hojicha_cups).toFixed(2)}g / cup` : "—"}</p>
            </div>
            <div className="bg-bone border-2 border-rose-700/40 rounded-sm p-4">
              <label className="overline text-rose-700 text-[10px] block">Complimentary (manual)</label>
              <input
                data-testid="sales-complimentary-cups"
                type="number"
                min="0"
                className={inputCls + " w-full mt-1 text-2xl font-display text-rose-700 border-0 p-0 focus:ring-0"}
                value={sales.complimentary_cups}
                onChange={(e) => setSales({ ...sales, complimentary_cups: Number(e.target.value) })}
              />
              <p className="text-[10px] text-ink-soft mt-0.5">Free / on-the-house cups</p>
            </div>
          </div>
        </section>

        {/* Save bar */}
        <div className="sticky bottom-0 -mx-6 md:-mx-10 px-6 md:px-10 py-4 bg-bone/95 backdrop-blur border-t border-ink/10 flex items-center justify-between gap-3 z-30">
          <div className="text-sm text-ink-soft">
            Total <span className="text-ink font-display text-lg ml-1">{totals.total_g.toFixed(0)}g</span>
            <span className="mx-2 opacity-40">·</span>
            <span className="text-matcha-dark">Matcha {totals.matcha_g.toFixed(0)}g</span>
            <span className="mx-2 opacity-40">·</span>
            <span className="text-amber-800">Hojicha {totals.hojicha_g.toFixed(0)}g</span>
            <span className="mx-2 opacity-40">·</span>
            <span className="text-stone-700">Coffee {totals.coffee_g.toFixed(0)}g</span>
          </div>
          <div className="flex items-center gap-3">
            {err && <span className="text-xs text-red-700 bg-red-50 px-2 py-1 rounded-sm">{err}</span>}
            {ok && <span className="inline-flex items-center gap-1 text-xs text-matcha-dark bg-sage/40 px-2 py-1 rounded-sm"><Check size={12} /> {ok}</span>}
            <button
              type="button"
              data-testid="no-record-btn"
              onClick={markNoRecord}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 border border-ink/20 text-ink text-xs uppercase tracking-[0.22em] hover:bg-cream/40 transition-colors disabled:opacity-50"
              title="Delete the log for this date and mark as no record"
            >
              No record
            </button>
            <button
              type="button"
              data-testid="save-btn"
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50"
            >
              <Save size={12} /> {busy ? "Saving…" : (exists ? "Overwrite" : "Save day")}
            </button>
          </div>
        </div>

        {/* Stock Tracker — calendar + usage/stock chart + reorder recommendation */}
        <PowderStockTracker month={chartMonth} onMonthChange={setChartMonth} onDayClick={setDate} />

        {/* Chart */}
        <section className="mt-10" data-testid="chart-section">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <p className="overline text-matcha-dark">Monthly progress</p>
              <h2 className="font-display text-xl text-ink" style={{ letterSpacing: "0.04em" }}>Total usage of the month</h2>
            </div>
            <p className="text-xs text-ink-soft">Chart covers every day of the selected month.</p>
          </div>
          <Chart month={chartMonth} onMonthChange={setChartMonth} refreshKey={chartRefresh} />
        </section>
      </div>
    </MgmtLayout>
  );
}

/**
 * StockDonut — animated SVG donut gauge for one powder kind.
 * Fills proportional to current_g / effective_capacity, where
 * effective_capacity = max(user_capacity, current_g) so the gauge never
 * caps at 100% when real stock exceeds the target — this way day-by-day
 * drain stays visible. Shows: % · current/capacity (kg) · optional delta
 * vs previous day · optional "exceeds target" hint.
 */
function StockDonut({ kind, current, capacity, previous = null, onCapacitySave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(capacity || 0));
  useEffect(() => { setDraft(String(capacity || 0)); }, [capacity]);

  const cur = Number(current) || 0;
  const cap = Math.max(1, Number(capacity) || 0);
  const effectiveCap = Math.max(cap, cur);           // never cap at 100%
  const pct = Math.max(0, Math.min(1, cur / effectiveCap));
  const vsTarget = cap > 0 ? cur / cap : 0;          // % of target capacity
  const overTarget = cur > cap;

  const stroke = overTarget ? (kind === "matcha" ? "#2f4a2a" : kind === "coffee" ? "#4a2e1a" : "#8d5a2c")
               : vsTarget >= 0.5 ? (kind === "matcha" ? "#2f4a2a" : kind === "coffee" ? "#4a2e1a" : "#8d5a2c")
               : vsTarget >= 0.2 ? "#c48a44"
                                 : "#b03a2e";
  const trackStroke = "rgba(0,0,0,0.08)";
  const R = 54;
  const C = 2 * Math.PI * R;
  const dash = C * pct;

  const label = kind === "matcha" ? "Matcha Baikuren"
              : kind === "coffee" ? "Coffee"
                                  : "Hojicha";
  const kg = (g) => (g / 1000).toFixed(g >= 10000 ? 1 : 2);
  const delta = previous != null ? cur - previous : null;

  const commit = async () => {
    const n = Math.max(0, Number(draft) || 0);
    await onCapacitySave?.(kind, n);
    setEditing(false);
  };

  return (
    <div className="flex flex-col items-center" data-testid={`stock-donut-${kind}`}>
      <div className="relative" style={{ width: 140, height: 140 }}>
        <svg viewBox="0 0 140 140" width="140" height="140" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="70" cy="70" r={R} fill="none" stroke={trackStroke} strokeWidth="14" />
          {pct > 0.001 && (
            <circle
              cx="70" cy="70" r={R}
              fill="none"
              stroke={stroke}
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${C - dash}`}
              style={{ transition: "stroke-dasharray 900ms cubic-bezier(.22,.9,.32,1), stroke 400ms" }}
              data-testid={`stock-donut-arc-${kind}`}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="font-display text-xl text-ink tabular-nums leading-none" data-testid={`stock-donut-grams-${kind}`}>
            {kg(cur)}<span className="text-[11px] text-ink-soft ml-0.5">kg</span>
          </p>
          <p className="text-[10px] text-ink-soft mt-0.5 tabular-nums">
            {Math.round(vsTarget * 100)}% of {kg(cap)}kg
          </p>
          {delta !== null && Math.abs(delta) >= 0.1 && (
            <p className={`text-[10px] font-medium mt-0.5 tabular-nums ${delta < 0 ? "text-red-700" : "text-matcha-dark"}`}
               data-testid={`stock-donut-delta-${kind}`}>
              {delta < 0 ? "▼" : "▲"} {Math.abs(delta).toFixed(1)}g vs prev day
            </p>
          )}
        </div>
      </div>
      <p className="overline text-ink mt-2 text-[11px]">{label}</p>
      {editing ? (
        <div className="flex items-center gap-1 mt-1">
          <input
            type="number"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            data-testid={`stock-donut-cap-input-${kind}`}
            className="w-20 border border-ink/15 rounded-sm px-2 py-0.5 text-xs text-right tabular-nums bg-bone"
            min="0"
            step="100"
          />
          <span className="text-[10px] text-ink-soft">g</span>
          <button
            type="button"
            onClick={commit}
            data-testid={`stock-donut-cap-save-${kind}`}
            className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 bg-ink text-bone rounded-sm hover:bg-matcha-dark transition-colors"
          >
            Save
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid={`stock-donut-cap-edit-${kind}`}
          className="text-[10px] text-ink-soft hover:text-ink mt-0.5 underline-offset-2 hover:underline"
        >
          Target = {kg(cap)}kg · edit
        </button>
      )}
      {overTarget && (
        <p className="text-[9px] text-amber-700 mt-0.5 italic">Stock exceeds target</p>
      )}
    </div>
  );
}

/**
 * PowderStockTracker — monthly matcha/hojicha stock story at a glance.
 *
 * Three stacked widgets:
 *  1. Reorder-recommendation cards (one per kind) — avg daily usage, days
 *     of runway, urgency badge, "buy Xg to hit 14-day target" CTA.
 *  2. Calendar grid for the month — one circle per day, intensity scales
 *     with that day's total usage; purchase days get a small ring. Click
 *     any day to jump the daily-log date picker to it.
 *  3. Dual-line chart — cumulative usage (climbs) vs stock-on-hand
 *     (falls, with bumps on purchase days). Both lines for matcha + hojicha
 *     overlaid on one chart so admins see the whole month at a glance.
 */
function PowderStockTracker({ month, onMonthChange, onDayClick }) {
  const [data, setData] = useState(null);
  const [kindView, setKindView] = useState("matcha");  // matcha | hojicha
  const [selectedDate, setSelectedDate] = useState(null); // ISO yyyy-mm-dd — null = today

  const loadData = async () => {
    try {
      const res = await api.get(`/mgmt/powder-tracker?month=${month}`);
      setData(res.data);
    } catch { /* silent — chart just stays empty */ }
  };

  useEffect(() => {
    let alive = true;
    setSelectedDate(null); // reset selection on month change
    (async () => {
      try {
        const res = await api.get(`/mgmt/powder-tracker?month=${month}`);
        if (alive) setData(res.data);
      } catch { /* silent — chart just stays empty */ }
    })();
    return () => { alive = false; };
  }, [month]);

  // Live refresh when the tab regains focus so Quick Usage taps show up.
  useEffect(() => {
    const refetch = () => {
      api.get(`/mgmt/powder-tracker?month=${month}`).then((r) => setData(r.data)).catch(() => {});
    };
    const onVis = () => { if (document.visibilityState === "visible") refetch(); };
    window.addEventListener("focus", refetch);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", refetch);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [month]);

  const saveCapacity = async (kind, grams) => {
    const curSettings = await api.get("/mgmt/settings").then((r) => r.data).catch(() => ({}));
    const payload = {
      outlet_investment: Number(curSettings.outlet_investment || 0),
      powder_capacity_matcha_g:  Number(curSettings.powder_capacity_matcha_g  || 10000),
      powder_capacity_hojicha_g: Number(curSettings.powder_capacity_hojicha_g || 5000),
      powder_capacity_coffee_g:  Number(curSettings.powder_capacity_coffee_g  || 5000),
    };
    payload[`powder_capacity_${kind}_g`] = grams;
    await api.put("/mgmt/settings", payload);
    await loadData();
  };

  const days = data?.days || [];
  const reco = data?.[kindView];
  const maxUsage = Math.max(1, ...days.map((d) => (d[`${kindView}_usage_g`] || 0)));

  // Which day's stock do the donuts and reco card show?
  // - If no date clicked → latest non-future day in the month (today's view).
  // - If a date is clicked → that specific day's running balance.
  const selectedDay = (() => {
    if (selectedDate) return days.find((d) => d.date === selectedDate);
    const latest = [...days].reverse().find((d) => !d.is_future);
    return latest || null;
  })();
  // Previous day (so the donut can render "▼ 15.9g vs prev day")
  const previousDay = (() => {
    if (!selectedDay) return null;
    const idx = days.findIndex((d) => d.date === selectedDay.date);
    return idx > 0 ? days[idx - 1] : null;
  })();
  const stockFor = (kind) => selectedDay ? (selectedDay[`${kind}_stock_g`] || 0)
                                         : (data?.[kind]?.current_stock_g || 0);
  const previousFor = (kind) => previousDay ? (previousDay[`${kind}_stock_g`] || 0) : null;
  const displayLabel = selectedDate
    ? `as of ${selectedDate}`
    : "live · today";

  // Chart data: cumulative usage climbing, stock falling with purchase bumps.
  const chartData = days.map((d) => ({
    day: d.day,
    date: d.date,
    usage: d[`${kindView}_cumulative_g`],
    stock: d[`${kindView}_stock_g`],
    purchase: d[`${kindView}_purchase_g`],
  }));

  // Calendar: pad with empties so the 1st of month lands on the right weekday.
  const firstDow = new Date(`${month}-01T00:00:00`).getDay(); // 0=Sun
  const cells = Array(firstDow).fill(null).concat(days);

  const urgencyColour = (u) => u === "critical" ? "bg-red-700 text-white"
    : u === "low" ? "bg-amber-600 text-white"
    : "bg-matcha-dark/80 text-white";

  return (
    <section className="mt-10 border-t border-ink/10 pt-10" data-testid="powder-tracker">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="overline text-matcha-dark">— Stock Tracker</p>
          <h2 className="font-display text-xl text-ink" style={{ letterSpacing: "0.04em" }}>
            Usage vs Stock · what to buy next
          </h2>
          <p className="text-ink-soft text-xs mt-1 max-w-2xl">
            Daily usage climbs, stock on hand falls — then bumps up each time you record a
            matcha / hojicha / coffee purchase. We watch your 14-day moving average and recommend
            how many grams to buy before you run out.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="month"
            data-testid="pt-month"
            value={month}
            onChange={(e) => onMonthChange(e.target.value)}
            className={inputCls}
          />
          <div className="inline-flex rounded-full border border-ink/15 overflow-hidden text-xs">
            {["matcha", "hojicha", "coffee"].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKindView(k)}
                data-testid={`pt-kind-${k}`}
                className={`px-4 py-1.5 uppercase tracking-[0.18em] transition-colors ${kindView === k ? "bg-ink text-bone" : "bg-bone text-ink hover:bg-cream/40"}`}
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Reorder reco cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="border border-ink/10 rounded-sm bg-cream/40 p-3" data-testid={`pt-${kindView}-current`}>
          <p className="overline text-ink-soft">Current stock</p>
          <p className="font-display text-2xl text-ink mt-1 tabular-nums">
            {stockFor(kindView).toFixed(0)}
            <span className="text-xs text-ink-soft ml-1">g</span>
          </p>
          <p className="text-[11px] text-ink-soft mt-1">
            {displayLabel} · Opening: {data?.opening_stock_g?.[kindView]?.toFixed(0) || 0}g
          </p>
        </div>
        <div className="border border-ink/10 rounded-sm bg-cream/40 p-3" data-testid={`pt-${kindView}-avg`}>
          <p className="overline text-ink-soft">Avg daily use</p>
          <p className="font-display text-2xl text-ink mt-1 tabular-nums">
            {reco ? reco.avg_daily_usage_g.toFixed(1) : "—"}
            <span className="text-xs text-ink-soft ml-1">g / day</span>
          </p>
          <p className="text-[11px] text-ink-soft mt-1">Rolling 14-day</p>
        </div>
        <div className="border border-ink/10 rounded-sm bg-cream/40 p-3" data-testid={`pt-${kindView}-runway`}>
          <p className="overline text-ink-soft">Days runway</p>
          <p className="font-display text-2xl text-ink mt-1 tabular-nums">
            {reco && reco.days_runway != null ? reco.days_runway.toFixed(1) : "—"}
            <span className="text-xs text-ink-soft ml-1">days</span>
          </p>
          {reco && (
            <span className={`inline-block mt-1 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${urgencyColour(reco.urgency)}`}>
              {reco.urgency === "critical" ? "Reorder now" : reco.urgency === "low" ? "Running low" : "On track"}
            </span>
          )}
        </div>
        <div className="border border-matcha-dark/50 rounded-sm bg-matcha-dark/5 p-3" data-testid={`pt-${kindView}-reco`}>
          <p className="overline text-matcha-dark">Recommend buy</p>
          <p className="font-display text-2xl text-matcha-dark mt-1 tabular-nums">
            {reco ? reco.recommended_buy_g.toFixed(0) : "—"}
            <span className="text-xs text-ink-soft ml-1">g</span>
          </p>
          <p className="text-[11px] text-ink-soft mt-1">
            Hit 14-day target of {reco ? reco.target_14_day_stock_g.toFixed(0) : "—"}g
          </p>
        </div>
      </div>

      {/* Live stock donuts beside the calendar */}
      <div className="grid grid-cols-2 md:grid-cols-[1fr_1fr_1fr_1.4fr] gap-4 mb-6" data-testid="pt-donuts-row">
        <div className="flex flex-col items-center justify-center bg-cream/30 border border-ink/10 rounded-sm py-5" data-testid="pt-donut-matcha-wrap">
          <StockDonut
            kind="matcha"
            current={stockFor("matcha")}
            previous={previousFor("matcha")}
            capacity={data?.capacity_g?.matcha || 10000}
            onCapacitySave={saveCapacity}
          />
          <p className="text-[9px] uppercase tracking-[0.18em] text-ink-soft mt-2">{displayLabel}</p>
        </div>
        <div className="flex flex-col items-center justify-center bg-cream/30 border border-ink/10 rounded-sm py-5" data-testid="pt-donut-hojicha-wrap">
          <StockDonut
            kind="hojicha"
            current={stockFor("hojicha")}
            previous={previousFor("hojicha")}
            capacity={data?.capacity_g?.hojicha || 5000}
            onCapacitySave={saveCapacity}
          />
          <p className="text-[9px] uppercase tracking-[0.18em] text-ink-soft mt-2">{displayLabel}</p>
        </div>
        <div className="flex flex-col items-center justify-center bg-cream/30 border border-ink/10 rounded-sm py-5" data-testid="pt-donut-coffee-wrap">
          <StockDonut
            kind="coffee"
            current={stockFor("coffee")}
            previous={previousFor("coffee")}
            capacity={data?.capacity_g?.coffee || 5000}
            onCapacitySave={saveCapacity}
          />
          <p className="text-[9px] uppercase tracking-[0.18em] text-ink-soft mt-2">{displayLabel}</p>
        </div>

        {/* Calendar grid */}
        <div className="rounded-sm border border-ink/10 bg-white/60 p-3 col-span-2 md:col-span-1" data-testid="pt-calendar">
          <div className="flex items-baseline justify-between mb-2">
            <p className="overline text-ink-soft text-[10px]">Daily usage · click a day</p>
            {selectedDate && (
              <button
                type="button"
                onClick={() => setSelectedDate(null)}
                data-testid="pt-clear-selection"
                className="text-[9px] uppercase tracking-[0.14em] text-matcha-dark hover:underline"
              >
                Reset
              </button>
            )}
          </div>
          <div className="grid grid-cols-7 gap-1 text-[9px] uppercase tracking-[0.12em] text-ink-soft text-center mb-1">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={`pad-${i}`} />;
              const usage = d[`${kindView}_usage_g`] || 0;
              const purchase = d[`${kindView}_purchase_g`] || 0;
              const intensity = usage > 0 ? Math.min(1, usage / maxUsage) : 0;
              const bg = usage === 0 ? "bg-ink/5 text-ink-soft"
                : kindView === "matcha" ? `bg-matcha-dark text-white`
                : kindView === "coffee" ? `bg-stone-700 text-white`
                : `bg-amber-700 text-white`;
              const isSelected = d.date === selectedDate;
              return (
                <button
                  key={d.date}
                  type="button"
                  onClick={() => { setSelectedDate(d.date); onDayClick?.(d.date); }}
                  data-testid={`pt-day-${d.date}`}
                  className={`relative aspect-square rounded-full flex items-center justify-center text-[11px] tabular-nums transition-transform hover:scale-110 ${bg} ${d.is_future ? "opacity-40" : ""} ${isSelected ? "ring-2 ring-ink ring-offset-1" : ""}`}
                  style={usage > 0 ? { opacity: 0.35 + intensity * 0.65 } : {}}
                  title={`${d.date} · ${usage.toFixed(0)}g used${purchase > 0 ? ` · +${purchase.toFixed(0)}g bought` : ""}`}
                >
                  {d.day}
                  {purchase > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-matcha-dark border border-bone"
                          title={`Purchase: ${purchase.toFixed(0)}g`} />
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-ink-soft mt-2 flex gap-2 flex-wrap leading-tight">
            <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-ink/40 mr-1 align-middle" />No log</span>
            <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-matcha-dark mr-1 align-middle" />Usage</span>
            <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-matcha-dark border border-bone mr-1 align-middle" />Buy</span>
          </p>
        </div>
      </div>

      {/* Line chart removed per user request */}
    </section>
  );
}
